import React, { useEffect, useMemo, useState } from 'react';
import { TransferItem, transferStatusColors } from '../stockManagementTypes';
import { useStockTransfersStore } from '../../../../store/stockTransfersStore';
import { StockTransfer, getTransfer } from '../../../../services/stockTransfersApi';
import { AsyncBoundary } from '../../../../components/AsyncBoundary';
import { CreateStockTransferModal } from './CreateStockTransferModal';
import styles from './TransfersTab.module.css';

const filters = ['All', 'In Transit', 'Completed', 'Cancelled'];

function adapt(t: StockTransfer): TransferItem {
  const totalUnits = (t.items ?? []).reduce((s, it) => s + it.quantity, 0);
  const status: TransferItem['status'] =
    t.status === 'in_transit'
      ? 'In Transit'
      : t.status === 'completed'
        ? 'Complete'
        : t.status === 'cancelled'
          ? 'Cancelled'
          : 'In Transit';
  return {
    id: t.transfer_number,
    date: new Date(t.requested_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    from: t.source_location,
    to: t.destination_location,
    items: `${(t.items ?? []).length} SKU${(t.items ?? []).length === 1 ? '' : 's'}`,
    units: totalUnits,
    value: '—',
    status,
    eta: '—',
  };
}

export const TransfersTab: React.FC = () => {
  const [activeFilter, setActiveFilter] = useState('All');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [trackedTransfer, setTrackedTransfer] = useState<StockTransfer | null>(null);
  const [tracking, setTracking] = useState(false);

  const items = useStockTransfersStore((s) => s.items);
  const loading = useStockTransfersStore((s) => s.loading);
  const error = useStockTransfersStore((s) => s.error);
  const fetchList = useStockTransfersStore((s) => s.fetchList);

  useEffect(() => { fetchList(); }, [fetchList]);

  const handleTrackOrView = async (transferNumber: string) => {
    const t = items.find((x) => x.transfer_number === transferNumber);
    if (!t) return;
    setTracking(true);
    try {
      const fresh = await getTransfer(t.id);
      setTrackedTransfer(fresh);
    } catch {
      setTrackedTransfer(t); // fall back to whatever's in the list
    } finally {
      setTracking(false);
    }
  };

  const adapted = useMemo<TransferItem[]>(() => items.map(adapt), [items]);
  const inTransitCount = adapted.filter((t) => t.status === 'In Transit').length;

  const filteredItems = useMemo(() => {
    if (activeFilter === 'All') return adapted;
    if (activeFilter === 'Completed') return adapted.filter((t) => t.status === 'Complete');
    return adapted.filter((t) => t.status === activeFilter as any);
  }, [adapted, activeFilter]);

  return (
    <div>
      <div className={styles.subHeader}>
        <p className={styles.subtitle}>Inventory monitoring — detect, investigate & correct in real time</p>
        <div className={styles.subActions}>
          <button
            type="button"
            className={styles.fullInvBtn}
            disabled
            title="Full inventory sync endpoint not yet available"
          >
            Full Inventory
          </button>
          <button
            type="button"
            className={styles.newBtn}
            onClick={() => setShowCreateModal(true)}
          >
            <svg viewBox="0 0 24 24" fill="currentColor"><path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/></svg>
            New Transfer
          </button>
        </div>
      </div>

      <div className={styles.filters}>
        {filters.map((f) => {
          const label = f === 'In Transit' ? `In Transit (${inTransitCount})` : f;
          const isActive = activeFilter === f;
          const isColored = f === 'In Transit' || f === 'Completed';
          return (
            <button
              key={f}
              className={`${styles.pill} ${isActive ? styles.pillActive : ''} ${!isActive && isColored && f === 'In Transit' ? styles.pillBlue : ''} ${!isActive && isColored && f === 'Completed' ? styles.pillGreen : ''}`}
              onClick={() => setActiveFilter(f)}
            >
              {label}
            </button>
          );
        })}
      </div>

      <AsyncBoundary
        loading={loading && items.length === 0}
        error={error}
        empty={!loading && filteredItems.length === 0}
        emptyMessage="No transfers to display"
        onRetry={() => fetchList()}
      >
        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>TRANSFER ID</th>
                <th>DATE</th>
                <th>FROM</th>
                <th>TO</th>
                <th>ITEMS</th>
                <th>UNITS</th>
                <th>VALUE</th>
                <th>STATUS</th>
                <th>ETA</th>
                <th>ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {filteredItems.map((item) => {
                const sc = transferStatusColors[item.status];
                return (
                  <tr key={item.id}>
                    <td className={styles.idCell}>{item.id}</td>
                    <td>{item.date}</td>
                    <td>{item.from}</td>
                    <td className={styles.bold}>{item.to}</td>
                    <td>{item.items}</td>
                    <td className={styles.bold}>{item.units}</td>
                    <td>{item.value}</td>
                    <td>
                      <span className={styles.statusBadge} style={{ color: sc.color }}>
                        <span className={styles.statusDot} style={{ background: sc.color }} />
                        {item.status}
                      </span>
                    </td>
                    <td>{item.eta}</td>
                    <td>
                      <button
                        type="button"
                        className={styles.actionBtn}
                        onClick={() => handleTrackOrView(item.id)}
                        disabled={tracking}
                      >
                        {item.status === 'In Transit' ? 'Track' : 'View'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </AsyncBoundary>

      <CreateStockTransferModal
        open={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          fetchList().catch(() => undefined);
        }}
      />

      {trackedTransfer && (
        <div
          className={styles.trackOverlay || ''}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 1000, padding: 24,
          }}
          onClick={() => setTrackedTransfer(null)}
          role="dialog"
          aria-modal="true"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: 'min(560px, 100%)', background: '#fff', borderRadius: 12,
              padding: 20, boxShadow: '0 24px 48px rgba(15,23,42,0.25)',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>{trackedTransfer.transfer_number}</h3>
              <button
                type="button"
                onClick={() => setTrackedTransfer(null)}
                style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#6B7280' }}
                aria-label="Close"
              >×</button>
            </div>
            <p style={{ fontSize: 13, color: '#374151', margin: '6px 0' }}>
              <strong>From:</strong> {trackedTransfer.source_location} → <strong>To:</strong> {trackedTransfer.destination_location}
            </p>
            <p style={{ fontSize: 13, color: '#374151', margin: '6px 0' }}>
              <strong>Status:</strong> {trackedTransfer.status}
            </p>
            <p style={{ fontSize: 12, color: '#6B7280', margin: '6px 0' }}>
              Requested {new Date(trackedTransfer.requested_at).toLocaleString()}
              {trackedTransfer.shipped_at && ` · Shipped ${new Date(trackedTransfer.shipped_at).toLocaleString()}`}
              {trackedTransfer.received_at && ` · Received ${new Date(trackedTransfer.received_at).toLocaleString()}`}
            </p>
            {trackedTransfer.notes && (
              <p style={{ fontSize: 12, color: '#6B7280', margin: '6px 0' }}>
                <strong>Notes:</strong> {trackedTransfer.notes}
              </p>
            )}
            <div style={{ marginTop: 12 }}>
              <h4 style={{ margin: '0 0 6px 0', fontSize: 12, textTransform: 'uppercase', color: '#6B7280' }}>Line Items</h4>
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {(trackedTransfer.items ?? []).map((it) => (
                  <li key={it.id} style={{ fontSize: 13, marginBottom: 2 }}>
                    {it.sku_code} — {it.product_name} · qty {it.quantity}
                  </li>
                ))}
                {(!trackedTransfer.items || trackedTransfer.items.length === 0) && (
                  <li style={{ fontSize: 12, color: '#9CA3AF' }}>No line items</li>
                )}
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
